import { useState } from 'react';
import { Info, RefreshCw, Star, Share2, Trash2, CheckCircle2, Loader2 } from 'lucide-react';
import SettingCard from '../../components/settings/SettingCard';
import SettingsRow from '../../components/settings/SettingsRow';

const APP_VERSION = '1.2.0';
const BUILD = 'msg-web-2026.07';

export default function About() {
  const [checking, setChecking] = useState(false);
  const [upToDate, setUpToDate] = useState(false);
  const [cacheCleared, setCacheCleared] = useState(false);

  const checkUpdates = () => {
    setChecking(true);
    setUpToDate(false);
    setTimeout(() => {
      setChecking(false);
      setUpToDate(true);
    }, 1200);
  };

  const share = async () => {
    const shareData = { title: 'Messego', text: 'Check out Messego — the unified platform for everything.', url: 'https://messego.com' };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch {
        /* dismissed */
      }
    } else {
      await navigator.clipboard.writeText(shareData.url);
    }
  };

  const clearCache = () => {
    // Clears only non-auth local caches used by the settings/data services.
    Object.keys(localStorage)
      .filter((k) => k.startsWith('messego.') && !k.includes('auth'))
      .forEach((k) => localStorage.removeItem(k));
    setCacheCleared(true);
    setTimeout(() => setCacheCleared(false), 2000);
  };

  return (
    <div className="space-y-6">
      <div className="hidden lg:block">
        <h1 className="text-2xl font-bold tracking-tight">About</h1>
        <p className="text-sm text-neutral-500 dark:text-neutral-400">App information and maintenance</p>
      </div>

      <SettingCard>
        <div className="flex flex-col items-center text-center py-4">
          <div className="w-16 h-16 rounded-3xl bg-neutral-900 flex items-center justify-center mb-4">
            <span className="text-white font-bold text-2xl">M</span>
          </div>
          <h2 className="text-xl font-bold">Messego</h2>
          <p className="text-sm text-neutral-500 dark:text-neutral-400">Unified platform for everything</p>
          <div className="mt-3 flex items-center gap-2 text-xs text-neutral-400 dark:text-neutral-500">
            <span className="font-mono">v{APP_VERSION}</span>
            <span>·</span>
            <span className="font-mono">{BUILD}</span>
          </div>
        </div>
      </SettingCard>

      <SettingCard flush>
        <SettingsRow
          icon={checking ? Loader2 : upToDate ? CheckCircle2 : RefreshCw}
          label={checking ? 'Checking for updates…' : upToDate ? "You're up to date" : 'Check for updates'}
          description={`Current version ${APP_VERSION}`}
          onClick={checkUpdates}
        />
        <SettingsRow icon={Share2} label="Share Messego" description="Invite friends to join" onClick={share} />
        <SettingsRow icon={Star} label="Rate the app" description="Leave a review" onClick={() => window.open('https://messego.com', '_blank', 'noreferrer')} />
        <SettingsRow
          icon={cacheCleared ? CheckCircle2 : Trash2}
          label={cacheCleared ? 'Cache cleared' : 'Clear cache'}
          description="Free up space used by local data (keeps you signed in)"
          onClick={clearCache}
        />
      </SettingCard>

      <SettingCard title="Details" flush>
        <SettingsRow icon={Info} label="Version" trailing={<span className="text-sm text-neutral-500 dark:text-neutral-400 font-mono">{APP_VERSION}</span>} />
        <SettingsRow icon={Info} label="Build" trailing={<span className="text-sm text-neutral-500 dark:text-neutral-400 font-mono">{BUILD}</span>} />
        <SettingsRow icon={Info} label="Platform" trailing={<span className="text-sm text-neutral-500 dark:text-neutral-400">Web</span>} />
      </SettingCard>

      <p className="text-center text-xs text-neutral-400 dark:text-neutral-500">Made with care by the Messego team.</p>
    </div>
  );
}
